import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

const AboutUs = () => {
  const milestones = [
    { year: "1985", title: "Foundation", description: "Varsha Seeds was established with a vision to transform Indian agriculture." },
    { year: "1992", title: "First Research Facility", description: "Opened our first seed research and development center." },
    { year: "2001", title: "Expansion", description: "Expanded operations to cover all major agricultural regions in India." },
    { year: "2010", title: "International Recognition", description: "Received international certification for seed quality and standards." },
    { year: "2018", title: "Technological Advancement", description: "Integrated advanced technology in seed processing and quality control." },
    { year: "2023", title: "Sustainability Initiative", description: "Launched eco-friendly seed varieties and sustainable farming practices." }
  ];

  const team = [
    { 
      name: "Rajesh Sharma", 
      position: "Founder & CEO", 
      image: "https://images.unsplash.com/photo-1560343776-97e7d202ff0e?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=300&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybWVyfHx8fHx8MTY5NzgzOTAzMw&ixlib=rb-4.0.3&q=80&w=300",
      bio: "With over 30 years of experience in agriculture, Rajesh has been the driving force behind Varsha Seeds' innovation and growth."
    },
    { 
      name: "Priya Patel", 
      position: "Head of Research", 
      image: "https://images.unsplash.com/photo-1597176116047-876a32798fcc?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=300&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybSB3b3JrZXJ8fHx8fHwxNjk3ODM5MDMz&ixlib=rb-4.0.3&q=80&w=300",
      bio: "Dr. Priya leads our research team, focusing on developing high-yield, disease-resistant seed varieties."
    },
    { 
      name: "Amit Kumar", 
      position: "Operations Director", 
      image: "https://images.unsplash.com/photo-1576721967382-55c0771b1605?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=300&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybWVyfHx8fHx8MTY5NzgzOTAzMw&ixlib=rb-4.0.3&q=80&w=300",
      bio: "Amit oversees all seed production operations, ensuring the highest quality standards are maintained."
    },
    { 
      name: "Sunita Verma", 
      position: "Sustainability Officer", 
      image: "https://images.unsplash.com/photo-1643059237608-a3900ab38ebc?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=300&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybSB3b3JrZXJ8fHx8fHwxNjk3ODM5MDMz&ixlib=rb-4.0.3&q=80&w=300",
      bio: "Sunita leads our initiatives for sustainable farming practices and environmental conservation."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - Varsha Seeds</title>
        <meta name="description" content="Learn about Varsha Seeds' journey, our mission, values, and the team behind our premium agricultural seed products." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary relative overflow-hidden py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YWdyaWN1bHR1cmFsIGxhbmRzY2FwZXN8fHx8fHwxNjk3ODM5MDgx&ixlib=rb-4.0.3&q=80&w=1920" 
            alt="Agricultural landscape" 
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6">
            Our Story
          </h1>
          <p className="text-white text-lg md:text-xl max-w-3xl mx-auto">
            For over three decades, Varsha Seeds has been dedicated to transforming 
            agriculture through innovation, quality, and sustainability.
          </p>
        </div>
      </section>
      
      {/* Our Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-medium">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6 font-heading">
                Empowering Farmers, Nurturing Nature
              </h2>
              <p className="text-neutral-700 mb-4">
                At Varsha Seeds, our mission is to empower farmers with superior quality 
                seeds that maximize yields while promoting sustainable agricultural practices.
              </p>
              <p className="text-neutral-700 mb-6">
                We believe in creating a balance between agricultural productivity and 
                environmental conservation, ensuring food security for future generations.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-neutral-700">
                    Develop high-yielding, disease-resistant seed varieties
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-neutral-700">
                    Provide comprehensive support to farmers from planting to harvest
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-neutral-700">
                    Promote environmentally conscious farming methods
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-1 flex-shrink-0">
                    <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-neutral-700">
                    Contribute to national food security and rural prosperity
                  </p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YWdyaWN1bHR1cmUgZmllbGRzfHx8fHx8MTY5NzgzODc5OA&ixlib=rb-4.0.3&q=80&w=600" 
                alt="Farmers in field" 
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-accent p-6 rounded-lg shadow-lg max-w-xs">
                <p className="text-white font-medium italic">
                  "Our dream is to see every farmer succeed with sustainable practices 
                  that nourish both people and the planet."
                </p>
                <p className="text-white font-bold mt-2">- Rajesh Sharma, Founder</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Journey */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Milestones in Our Growth
            </h2>
            <p className="text-neutral-700">
              From humble beginnings to becoming a trusted name in agricultural seeds, 
              our journey has been marked by continuous innovation and dedication.
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-neutral-200 transform -translate-x-1/2 hidden md:block"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-8 relative ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="md:w-1/2 flex justify-center md:justify-end">
                    <div className={`bg-white p-6 rounded-lg shadow-md max-w-md ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <div className="text-xl font-bold text-primary mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold mb-2 font-heading">{milestone.title}</h3>
                      <p className="text-neutral-700">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2 hidden md:block"></div>
                  
                  <div className="absolute left-1/2 top-6 w-6 h-6 bg-primary rounded-full transform -translate-x-1/2 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Principles That Guide Us
            </h2>
            <p className="text-neutral-700">
              Our core values shape every decision we make and every seed we produce.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-background p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 font-heading">Quality</h3>
              <p className="text-neutral-700">
                We maintain the highest standards in all our seed varieties, ensuring 
                optimal performance and yield.
              </p>
            </div>
            
            <div className="bg-background p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 font-heading">Innovation</h3>
              <p className="text-neutral-700">
                We continuously research and develop new seed varieties and technologies 
                to meet evolving agricultural challenges.
              </p>
            </div>
            
            <div className="bg-background p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 font-heading">Sustainability</h3>
              <p className="text-neutral-700">
                We are committed to environmentally responsible practices that protect 
                soil health and biodiversity.
              </p>
            </div>
            
            <div className="bg-background p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 font-heading">Farmer-Centric</h3>
              <p className="text-neutral-700">
                We prioritize farmers' needs and success, providing them with products 
                and support that enhance their livelihoods.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Team */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Our Team</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              The People Behind Varsha Seeds
            </h2>
            <p className="text-neutral-700">
              Our diverse team of agricultural experts, researchers, and professionals work 
              together to deliver excellence in every seed we produce.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-md transition hover:shadow-lg">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 font-heading">{member.name}</h3>
                  <p className="text-primary font-medium mb-3">{member.position}</p>
                  <p className="text-neutral-700">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white font-heading">
              Join Us in Transforming Agriculture
            </h2>
            <p className="text-white text-opacity-90 mb-8 max-w-3xl mx-auto">
              Whether you're a farmer looking for premium seeds or a professional interested in 
              joining our team, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link href="/products">
                <Button variant="secondary" size="lg" className="rounded-full bg-white hover:bg-gray-100 text-primary">
                  Explore Our Products
                </Button>
              </Link>
              <Link href="/contact">
                <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-white">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUs;
